import { DateTime } from 'luxon';

export const DEFAULT_MAX_CACHE_ENTRIES = 10;

let cache = { propertyId: null, entries: {} };

/**
 * Resets the TradeList day cache for the active property.
 * @param {string|null} propertyId - Property identifier the cache belongs to.
 * @returns {{propertyId: string|null, entries: object}} Fresh cache object.
 */
export const initialiseTradeListCache = (propertyId) => {
  cache = { propertyId, entries: {} };
  return cache;
};

/**
 * Returns the current module-scoped TradeList cache object.
 *
 * Used by useTradeData for cache writes and by tests for assertions.
 * Not part of the public API — prefer getTradeListDataFromCache for lookups.
 * @internal
 * @returns {{propertyId: string|null, entries: object}} Current cache state.
 */
export const getTradeListCache = () => cache;

/**
 * Builds the cache key for a requested TradeList day.
 * @param {DateTime|string|null|undefined} start - Requested day start value.
 * @returns {string|null} ISO date key when the input is valid, otherwise null.
 */
export const getDayCacheKey = (start) => {
  if (!start) {
    return null;
  }

  if (typeof start?.toISODate === 'function') {
    return start.toISODate();
  }

  return null;
};

/**
 * Looks up a cached TradeList entry by exact day key.
 * @param {{entries?: object}|null|undefined} cacheObj - Cache object to inspect.
 * @param {string|null|undefined} dayKey - ISO day key to match.
 * @returns {object|null} Cached entry for the requested day, if present.
 */
export const lookupTradeListCacheEntry = (cacheObj, dayKey) => {
  if (!cacheObj?.entries || !dayKey) {
    return null;
  }

  return cacheObj.entries[dayKey] || null;
};

/**
 * Inserts or replaces a cached TradeList day entry and evicts the oldest entry at capacity.
 * @param {{entries?: object}|null|undefined} cacheObj - Cache object to mutate.
 * @param {string|null|undefined} dayKey - ISO day key for the cache slot.
 * @param {object|null|undefined} entry - Cached payload for the requested day.
 * @returns {void}
 */
export const addTradeListCacheEntry = (cacheObj, dayKey, entry) => {
  if (!cacheObj?.entries || !dayKey || !entry) {
    return;
  }

  const { entries } = cacheObj;
  const keys = Object.keys(entries);

  if (!entries[dayKey] && keys.length >= DEFAULT_MAX_CACHE_ENTRIES) {
    delete entries[keys[0]];
  }

  entries[dayKey] = {
    dateKey: dayKey,
    ...entry,
    lastUpdatedAt: Date.now(),
  };
};

/**
 * Returns cached TradeList data for the requested property/day pair, resetting on property change.
 * @param {object} params - Cache lookup parameters.
 * @param {string|null|undefined} params.propertyId - Property identifier for cache scoping.
 * @param {DateTime|string|null|undefined} params.start - Requested day start value.
 * @returns {object|null} Cached TradeList entry for the requested day, if present.
 */
export const getTradeListDataFromCache = ({
  propertyId,
  start,
}) => {
  if (cache.propertyId !== propertyId) {
    cache = initialiseTradeListCache(propertyId);
  }

  const dayKey = getDayCacheKey(start);
  if (!dayKey) {
    return null;
  }

  return lookupTradeListCacheEntry(cache, dayKey);
};
